/**
 * i18n/he.ts — every user-visible string of the personal web app.
 *
 * ARCHITECTURE §10: "Copy is templated: i18n/he.ts holds every string (no literals in
 * components); card masks, names, counts, dates and plan/credit figures come from the API.
 * Where a value is unavailable (credits used, plan) the component renders the mockup's label
 * with 'לא זמין', never a demo number."
 *
 * Wording is the approved mockup's (`mockups/personal.html`) and spec-personal.md §4/§5.
 */

import type { AccountType, AlertType, ConnectionStatus, CreditStatus, GroupKey, ProviderId, Scope } from '../../../shared-types.ts';

/** Hebrew label + Lucide-style icon key for each of the 10 display groups (spec §7.3). */
export const CATEGORY: Readonly<Record<GroupKey, { label: string; icon: string }>> = {
  HOUSING: { label: 'דיור וחשבונות', icon: 'home' },
  GROCERIES: { label: 'מזון וסופר', icon: 'basket' },
  KIDS: { label: 'ילדים וחינוך', icon: 'graduation' },
  TRANSPORT: { label: 'תחבורה ורכב', icon: 'car' },
  DINING: { label: 'מסעדות ובילוי', icon: 'utensils' },
  SHOPPING: { label: 'קניות וביגוד', icon: 'bag' },
  INSURANCE: { label: 'ביטוח', icon: 'shield' },
  HEALTH: { label: 'בריאות', icon: 'heart' },
  TELECOM: { label: 'תקשורת ומנויים', icon: 'phone' },
  OTHER: { label: 'אחר', icon: 'dashed' },
};

/**
 * The provider's Hebrew name. The PICTURE is drawn by `ProviderMark` (REDESIGN §7a) from
 * `shared/ui`, so the old text `mark` (a letter, and one ✎ glyph) is gone — an institution is
 * identified by its drawn monogram now, and this map holds only the accessible name.
 */
export const PROVIDER: Readonly<Record<string, { name: string }>> = {
  discount: { name: 'דיסקונט' },
  max: { name: 'Max' },
  cal: { name: 'Cal' },
  manual: { name: 'ידני' },
};

export function providerName(id: ProviderId): string {
  return PROVIDER[id]?.name ?? String(id);
}

export const ACCOUNT_TYPE: Readonly<Record<AccountType, string>> = {
  CHECKING: 'עו"ש',
  SAVINGS: 'פיקדון',
  SECURITY: 'ניירות ערך',
  LOAN: 'הלוואה',
  CARD: 'כרטיס אשראי',
};

export const CONNECTION_STATUS: Readonly<Record<ConnectionStatus, { label: string; chip: string; icon: string }>> = {
  ACTIVE: { label: 'פעיל', chip: 'good', icon: 'check' },
  COMPLETED: { label: 'משיכה חד-פעמית', chip: 'muted', icon: 'clock' },
  EXPIRED: { label: 'פג תוקף', chip: 'critical', icon: 'alert' },
  REVOKED: { label: 'בוטל', chip: 'muted', icon: 'x' },
  REJECTED: { label: 'נדחה', chip: 'critical', icon: 'alert' },
  FETCHING: { label: 'מתחבר…', chip: 'muted', icon: 'clock' },
};

export const CREDIT_STATUS: Readonly<Record<CreditStatus, { label: string; chip: string; icon: string }>> = {
  enabled: { label: 'פעיל', chip: 'good', icon: 'check' },
  blocked: { label: 'חסום', chip: 'warn', icon: 'lock' },
  deleted: { label: 'מחוק', chip: 'muted', icon: 'x' },
};

export const SCOPE_LABEL: Readonly<Record<Scope, string>> = {
  spend: 'הוצאה',
  income: 'הכנסה',
  transfer: 'העברה פנימית',
  'card-charge': 'חיוב כרטיס',
  savings: 'הפקדה לחיסכון',
  tax: 'מס / ביטוח לאומי',
  'fee-return': 'הו"ק שחזרה',
  ignore: 'לא נספר',
};

export const ALERT_ICON: Readonly<Record<AlertType, string>> = {
  card_due_soon: 'card',
  coverage_low: 'wallet',
  category_over_baseline: 'bars',
  large_transaction: 'receipt',
  returned_payment: 'repeat',
  connection_expired: 'link',
  data_stale: 'clock',
  refresh_failed: 'refresh',
  new_account: 'plus',
};

export const MONTHS = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'] as const;
export const MONTHS_SHORT = ['ינו׳', 'פבר׳', 'מרץ', 'אפר׳', 'מאי', 'יוני', 'יולי', 'אוג׳', 'ספט׳', 'אוק׳', 'נוב׳', 'דצמ׳'] as const;
export const DAYS = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'] as const;

/** All flat copy. Functions are the templated strings. */
export const he = {
  app: {
    name: 'הבית שלנו',
    household: 'יאיר ומיכל',
    skip: 'דלג לתוכן',
    unavailable: 'לא זמין',
    loading: 'טוען',
    close: 'סגור',
    cancel: 'ביטול',
    save: 'שמור',
    edit: 'ערוך',
    change: 'שנה',
    apply: 'החל',
    clear: 'נקה',
    back: 'חזרה',
    undo: 'בטל',
    details: 'פרטים',
    all: 'הכול',
    search: 'חיפוש',
    manual: 'הזנה ידנית',
  },
  nav: {
    home: 'ראשי',
    transactions: 'עסקאות',
    categories: 'קטגוריות',
    cards: 'כרטיסים',
    loans: 'הלוואות',
    networth: 'שווי נקי',
    alerts: 'התראות',
    alertsCenter: 'מרכז התראות',
    settings: 'הגדרות וחיבורים',
    more: 'עוד',
    logout: 'יציאה',
    signOut: 'התנתק',
    mainNav: 'ניווט ראשי',
    bottomNav: 'ניווט תחתון',
  },
  roles: { owner: 'בעלים', member: 'חבר/ה', memberF: 'חברה' },
  top: {
    refresh: 'רענן נתונים',
    refreshShort: 'רענן',
    refreshMember: 'רק יאיר יכול לרענן',
    notifications: 'התראות',
    userMenu: 'תפריט משתמש',
    clickToRefresh: 'לחץ לרענון',
  },
  freshness: {
    none: 'אין חיבורים פעילים',
    warn: 'עודכן לפני יותר מיום',
    fresh: (rel: string) => `עודכן: ${rel}`,
    critical: (days: number) => `הנתונים ישנים (${days} ימים)`,
  },
  period: {
    label: 'תקופה',
    month: (m: string) => `החודש (${m})`,
    prev: (m: string) => `חודש קודם (${m})`,
    m3: '3 חודשים אחרונים',
    m12: '12 חודשים אחרונים',
    custom: 'טווח מותאם (עד 12 חודשים)',
    from: 'מתאריך',
    to: 'עד תאריך',
    tooLong: 'טווח מותאם מוגבל ל-12 חודשים',
    lastMonthChip: (m: string) => `חריגה לפי החודש האחרון בטווח — ${m}`,
    lastMonthChipShort: 'חריגה לפי החודש האחרון בטווח',
    rangeNote: 'סכומים על כל הטווח',
    rangeNoteCmp: 'סכומים על כל הטווח · ההשוואה לטווח קודם באותו אורך',
    noPrev: 'אין תקופה קודמת להשוואה',
    vs: (label: string, value: string) => `לעומת ${label}: ${value}`,
    noChange: 'ללא שינוי',
  },
  banner: {
    regionLabel: 'חיבור פג תוקף',
    one: (name: string, when: string, at: string) =>
      `החיבור ל-${name} פג תוקף ${when}. הנתונים מוצגים נכון ל-${at}. פתח את אפליקציית Financy ← חבר מחדש.`,
    manyTitle: (n: number) => `${n} חיבורים פגי תוקף:`,
    manyBody: 'הנתונים מוצגים כפי שהיו במשיכה האחרונה. פתח את אפליקציית Financy ← חבר מחדש.',
    today: 'היום',
    on: (d: string) => `ב-${d}`,
    openFinancy: 'פתח את Financy',
    hideToday: 'הסתר להיום',
    hidden: 'הבאנר הוסתר להיום. החיבור עדיין פג תוקף.',
  },
  refresh: {
    title: 'רענון נתונים',
    body: (left: string, total: string) => `רענון עולה 20 קרדיטים (נשארו ${left} מתוך ${total} החודש). לרענן?`,
    bodyUnknown: 'רענון עולה 20 קרדיטים. מספר הקרדיטים שנותרו — לא זמין. לרענן?',
    confirm: 'רענן (20 קרדיטים)',
    noActive: 'אין חיבורים פעילים לרענון. חבר מחדש באפליקציית Financy.',
    toConnections: 'לחיבורים',
    ok: 'הנתונים עודכנו לפני רגע',
    failed: 'הרענון נכשל — Financy לא הגיב. נסה שוב בעוד דקה.',
    retry: 'נסה שוב',
    cooldown: (at: string) => `רענון נוסף יתאפשר ב-${at}.`,
    inProgress: 'רענון כבר רץ',
    started: 'הרענון החל…',
  },
  states: {
    emptyPeriodTitle: 'אין עסקאות בתקופה הזו',
    emptyPeriodBody: 'נסה טווח אחר או רענן את הנתונים',
    emptyMonthTitle: (m: string) => `אין עסקאות ב${m} עדיין`,
    currentMonth: 'החודש הנוכחי',
    noDataTitle: 'עדיין אין נתונים',
    noDataBody: 'חבר את דיסקונט, Max ו-Cal באפליקציית Financy ואז רענן כאן.',
    serverError: 'שגיאת שרת — מוצגים הנתונים האחרונים שיש',
    notUpdated: 'לא עודכן',
    loadFailed: 'טעינת הנתונים נכשלה',
    tryAgain: 'נסה שוב',
  },
  home: {
    flowTitle: (period: string) => `תזרים — ${period}`,
    available: (mask: string) => `זמין בעו"ש ${mask}`,
    availableNoMask: 'זמין בעו"ש',
    charges30: 'חיובים ב-30 יום',
    charges30Aria: 'חיובי אשראי ב-30 הימים הקרובים',
    noChargesWindow: 'אין חיובים בחלון',
    leftAfter: 'נשאר אחרי החיובים',
    covered: 'יש כיסוי',
    missing: (amount: string) => `חסר ${amount}`,
    knownAmounts: 'לפי הסכומים הידועים',
    returnsTitle: 'החזרות ועמלות החודש',
    returnsAria: 'החזרות ועמלות החודש — לרשימת ההחזרות',
    returnsNone: 'ללא החזרות — הו"ק ושיקים עברו',
    returnsOne: 'הוראת קבע אחת חזרה',
    returnsMany: (n: number) => `${n} הוראות קבע/שיקים חזרו`,
    returnsFees: (amount: string) => `${amount} עמלות`,
    in: 'נכנס',
    out: 'יצא',
    net: 'נטו',
    inAria: 'נכנס — כל ההכנסות בתקופה',
    outAria: 'יצא — כל ההוצאות בתקופה',
    progress: (day: number, days: number, left: number) => `יום ${day} מתוך ${days} · נותרו ${left} ימים`,
    progressAria: 'התקדמות החודש',
    chargeMark: (d: string) => `חיוב ${d}`,
    expectedIncome: (list: string) => `הכנסות צפויות עד סוף החודש: ${list}`,
    includingTag: (names: string, date: string) => `כולל ${names} נכון ל-${date}`,
    chargesTitle: 'חיובי אשראי קרובים',
    toCards: 'לכרטיסים',
    noCards: 'אין כרטיסי אשראי מחוברים',
    final: 'סופי',
    accrued: 'נצבר עד כה',
    accruedShort: 'נצבר',
    moreChargesOne: 'עוד חיוב אחד',
    moreChargesMany: (n: number) => `עוד ${n} חיובים`,
    coverageLine: (charges: string, avail: string) => `חיובים ב-30 יום: ${charges} · זמין בעו"ש: ${avail}`,
    coverageLeft: (amount: string) => `(נשאר ${amount})`,
    coverageNoteOpen: (card: string, end: string, expected: string) =>
      `מחזור ${card} פתוח עד ${end} — לפי הצפי (≈ ${expected})`,
    coverageStillOk: (amount: string) => `עדיין יש כיסוי (≈ ${amount})`,
    coverageWillMiss: (amount: string) => `יחסרו ≈ ${amount}`,
    coverageExclusions: 'לא כולל הוראות קבע והכנסות צפויות',
    catsTitle: 'הוצאות לפי קטגוריה',
    catsSub: '— לעומת ממוצע 3 חודשים',
    allCats: 'כל הקטגוריות',
    noCats: 'אין הוצאות בקטגוריות החודש',
    moreCatsOne: 'עוד קטגוריה אחת',
    moreCatsMany: (n: number) => `עוד ${n} קטגוריות`,
    baseline: (amount: string) => `בסיס ${amount}`,
    baselineManual: 'ידני',
    noBaseline: 'אין בסיס עדיין',
    over: (ratio: string) => `חריגה ×${ratio}`,
    nwTitle: 'שווי נקי',
    nwMonth: (amount: string, pct: string) => `החודש: ${amount} (${pct})`,
    nwAssetsLine: (assets: string, liabilities: string) => `נכסים ${assets} · התחייבויות ${liabilities} · ני"ע: לא מחובר`,
    nwPeriod: 'ספטמבר 2025 – היום',
    nwSparkAria: (from: string, to: string) => `שווי נקי 12 חודשים: מ-${from} ל-${to}`,
    alertsTitle: (n: number) => `דורש תשומת לב (${n})`,
    allAlerts: 'כל ההתראות',
    markRead: 'סמן כנקרא',
    markedRead: 'ההתראה סומנה כנקראה',
    recentTitle: '5 עסקאות אחרונות',
    allTx: 'כל העסקאות',
    accountsTitle: 'החשבונות',
    accountsFoot: 'ני"ע: לא מחובר',
    assignAccounts: 'שיוך חשבונות',
    availableSuffix: 'זמין',
  },
  /** REDESIGN §3/§4 — the narrative block, the one hero figure and the urgency lists. */
  now: {
    narrativeTitle: 'מה קורה',
    heroLabel: 'נשאר אחרי החיובים',
    heroInputs: (available: string, charges: string) => `זמין ${available} · חיובים ${charges}`,
    covered: 'יש כיסוי',
    missing: (gap: string) => `חסר ${gap}`,
    coverageUnknown: 'אין נתוני כיסוי',
    late: 'באיחור',
    moreOne: 'עוד פריט אחד',
    moreMany: (n: number) => `עוד ${n} פריטים`,
    less: 'הצג פחות',
    laterOne: 'דבר אחד שאפשר לדחות',
    laterMany: (n: number) => `${n} דברים שאפשר לדחות`,
    show: 'הצג',
    hide: 'הסתר',
    allClearTitle: 'אין מה לעשות עכשיו',
    allClearBody: 'אין חיובים קרובים, איחורים או תשלומים שנכשלו.',
    reconnect: 'חבר מחדש',
    expiredLine: (names: string, at: string) => `החיבור ל-${names} פג תוקף · הנתונים נכון ל-${at}`,
  },
  /** REDESIGN §4.6 — the quiet detail strip. Each block: heading, ≤5 lines, one link. */
  strip: {
    flow: 'תזרים החודש',
    flowLink: 'כל העסקאות',
    flowTblDir: 'כיוון',
    flowTblAmount: 'סכום',
    cats: 'קטגוריות',
    catsLink: 'כל הקטגוריות',
    catsNone: 'אין חריגה מהבסיס החודש',
    cards: 'כרטיסים',
    cardsLink: 'לכרטיסים',
    cardsLine: (n: number, used: string) => `${n} כרטיסים פעילים · חוב ${used}`,
    loans: 'הלוואות',
    loansLink: 'להלוואות',
    nw: 'שווי נקי',
    nwLink: 'לשווי הנקי',
    accounts: 'חשבונות',
    accountsLink: 'לחשבונות',
    accountsLine: (n: number, total: string) => `${n} חשבונות · יתרה ${total}`,
  },
  /** REDESIGN §6 — the loans screen. Every figure it labels comes from `GET /api/loans`. */
  loans: {
    title: 'הלוואות',
    debtTotal: 'סך החוב',
    monthly: 'תשלום חודשי',
    weightedRate: 'ריבית משוקללת',
    weightedRateNote: 'ממוצע משוקלל לפי היתרות',
    yearInterest: 'הריבית שתשלם השנה',
    yearInterestNote: 'אומדן 12 חודשים לפי היתרה, הריבית והתשלום',
    debtOf: (n: number) => `${n} הלוואות פתוחות`,
    debtOfOne: 'הלוואה אחת פתוחה',
    monthlyEstimatedOf: (amount: string) => `מזה ${amount} באומדן`,
    monthlyAllKnown: 'לפי התשלומים הידועים',
    scope: 'היקף',
    scopeAll: 'הכל',
    scopePersonal: 'פרטי',
    scopeBusiness: 'עסקי',
    trendQ: 'לאן החוב הולך?',
    trendPeriod: (from: string, to: string) => `${from} – ${to}`,
    trendDown: (amount: string) => `יורד ב-${amount} בחודש בקצב הנוכחי`,
    trendUp: (amount: string) => `עולה ב-${amount} בחודש בקצב הנוכחי`,
    trendFlat: 'ללא שינוי בחוב בחודש האחרון',
    trendAllScopes: 'המגמה מוצגת על כל ההלוואות, לא רק על הסינון הנוכחי',
    trendTblMonth: 'חודש',
    trendTblDebt: 'חוב',
    rankTitle: 'הכי יקר קודם',
    // §6 keeps card facilities out of the bank list, so the order is descending WITHIN each group
    // and the groups themselves lead with the most expensive. The sub-line says exactly that
    // instead of promising one global descending list.
    rankSub: '— בכל קבוצה מהריבית הגבוהה לנמוכה, והקבוצה היקרה קודם',
    rankSubOther: '— קרדיט בכרטיסים מקובץ תחת הכרטיס',
    topRate: 'היקרה ביותר',
    topRateOf: (name: string, rate: string) => `${name} — ${rate}`,
    cardFacilities: (n: number) => `${n} מסגרות`,
    cardFacilityOne: 'מסגרת אחת',
    groupBalance: 'יתרה',
    moreOne: 'עוד הלוואה אחת',
    moreMany: (n: number) => `עוד ${n} הלוואות`,
    less: 'הצג פחות',
    sortLabel: 'מיון',
    sortRate: 'ריבית',
    sortBalance: 'יתרה',
    sortMonthly: 'תשלום חודשי',
    sortEnd: 'תאריך סיום',
    bankGroup: 'הלוואות בנק',
    cardGroup: 'קרדיט בכרטיסים',
    cardCreditOf: (card: string) => `קרדיט בכרטיס ${card}`,
    ratePct: (pct: string) => `${pct}%`,
    noRate: 'ריבית לא זמינה',
    monthlyShort: 'לחודש',
    ends: (d: string) => `מסתיימת ${d}`,
    endsInMonths: (n: number) => (n === 1 ? 'עוד חודש' : n === 2 ? 'עוד חודשיים' : `עוד ${n} חודשים`),
    noEnd: 'ללא תאריך סיום',
    estimated: 'מוערך',
    yearly: (amount: string) => `ריבית שנתית ≈ ${amount}`,
    rateFixed: 'ריבית קבועה',
    rateIndexed: (index: string) => `צמודה ל${index}`,
    ratePrimePlus: (index: string, margin: string) => `${index} + ${margin}%`,
    rateUnknownType: 'סוג ריבית לא ידוע',
    rateStructure: 'מבנה הריבית',
    indexDefault: 'פריים',
    tierKind: 'סוג',
    tierRate: 'ריבית',
    tierRange: 'מדרגה',
    tierAll: 'כל היתרה',
    contract: 'תקופת ההלוואה',
    contractFrom: (d: string) => `מ-${d}`,
    remainingLabel: 'תשלומים נותרים',
    remaining: (n: number) => `${n} תשלומים`,
    paymentSource: {
      note: 'הוזן ידנית',
      history: 'נגזר מההיסטוריה',
      amortized: 'אומדן לפי לוח סילוקין',
      none: 'לא זמין',
    } as Readonly<Record<string, string>>,
    toTransactions: 'לעסקאות של ההלוואה',
    timelineTitle: 'מתי מתפנה כסף',
    timelineQ: '— מתי כל הלוואה נגמרת',
    timelineFree: (months: number, count: number, amount: string) =>
      `בעוד ${months} חודשים ייגמרו ${count} הלוואות ויתפנו ${amount} בחודש`,
    timelineFreeOne: (months: number, amount: string) =>
      `בעוד ${months} חודשים תיגמר הלוואה אחת ויתפנו ${amount} בחודש`,
    timelineFreeNoAmount: (months: number, count: number) => `בעוד ${months} חודשים ייגמרו ${count} הלוואות`,
    timelineFreeNoAmountOne: (months: number) => `בעוד ${months} חודשים תיגמר הלוואה אחת`,
    timelineNone: 'אין הלוואה עם תאריך סיום ידוע בטווח',
    timelineScoped: 'המשפט מחושב על כל ההלוואות — בחר "הכל" כדי לראות אותו מלא',
    timelineBeyond: (n: number, year: number) => `${n} מסתיימות אחרי ${year}`,
    timelineUndated: (n: number) => `${n} ללא תאריך סיום`,
    closedCount: (n: number) => (n === 1 ? 'הלוואה אחת' : `${n} הלוואות`),
    closedNote: 'עברו את תאריך הסיום החוזי ועדיין רשומה להן יתרה. אינן נספרות בסך החוב ובתשלום החודשי.',
    empty: 'אין הלוואות',
    emptyBody: 'כשתהיה הלוואה פעילה באחד החיבורים — היא תופיע כאן.',
    emptyScope: 'אין הלוואות בסינון הזה',
    emptyScopeBody: 'נסה "הכל", או שנה את שיוך החשבונות בהגדרות.',
    business: 'עסקי',
  },
  /** REDESIGN §7d — illustrated empty and healthy states. */
  empty: {
    noAnomalies: { title: 'אין חריגות החודש', body: 'כל הקטגוריות בטווח הבסיס ואין תשלומים שנכשלו.' },
    noAlerts: { title: 'אין התראות', body: 'כשמשהו ידרוש תשומת לב — הוא יופיע כאן.' },
    noResults: { title: 'אין תוצאות', body: 'נסה טווח אחר או נקה את הסינון.' },
    reconnect: { title: 'צריך לחבר מחדש', body: 'תוקף ההסכמה פג. פתח את אפליקציית Financy וחבר מחדש.' },
    firstRun: { title: 'עדיין אין נתונים', body: 'חבר את דיסקונט, Max ו-Cal באפליקציית Financy ואז רענן כאן.' },
  },
  tx: {
    account: 'חשבון/כרטיס',
    allAccounts: 'כל החשבונות',
    category: 'קטגוריה',
    categories: (n: number) => `${n} קטגוריות`,
    searchPlaceholder: 'חיפוש בית עסק או סכום',
    status: 'סטטוס',
    statusAll: 'הכול',
    pending: 'ממתין',
    booked: 'נרשם',
    clearFilter: 'נקה סינון',
    filter: 'סינון',
    returnsOnly: 'החזרות ועמלות בלבד',
    expenses: 'הוצאות',
    incomes: 'הכנסות',
    count: 'עסקאות',
    ofWhichPendingOne: 'מתוכן 1 ממתינה',
    ofWhichPendingMany: (n: number) => `מתוכן ${n} ממתינות`,
    range: 'טווח',
    backToPeriod: 'חזרה לתקופה הגלובלית',
    colDate: 'תאריך',
    colMerchant: 'בית עסק',
    colCategory: 'קטגוריה',
    colInstallments: 'תשלומים',
    colAmount: 'סכום',
    colStatus: 'סטטוס',
    sortByAmount: 'מיין לפי סכום',
    loadMore: 'טוען עוד…',
    loadMoreOne: 'עסקה אחת נוספת',
    loadMoreMany: (n: number) => `${n} נוספות`,
    emptyFiltered: 'אין עסקאות שתואמות את הסינון',
    showN: (n: number) => (n === 1 ? 'עסקה אחת' : `${n} עסקאות`),
    show: 'הצג',
    detailLabel: 'פרטי עסקה',
    txDate: 'תאריך עסקה',
    booking: 'רישום',
    awaitingBooking: 'ממתין לרישום',
    accountLabel: 'חשבון',
    installmentsOf: (n: number, total: number) => `${n} מתוך ${total}`,
    installmentsLeft: (n: number, amount: string) => `נותרו ${n} × ${amount}`,
    installmentsLast: 'אחרון',
    installmentsTotal: (amount: string) => `סה"כ העסקה ${amount}`,
    origCurrency: 'מטבע מקורי',
    foreignChip: 'מטבע זר',
    balanceAfterCard: 'יתרת הכרטיס אחרי העסקה',
    balanceAfterChecking: 'יתרה בעו"ש אחרי העסקה',
    note: 'הערה',
    notePlaceholder: 'הוסף הערה…',
    noteSaved: 'ההערה נשמרה',
    addNote: 'הוסף הערה',
    markInternal: 'סמן כהעברה פנימית',
    unmarkInternal: 'בטל סימון "העברה פנימית"',
    markInternalShort: 'סמן פנימי',
    unmarkInternalShort: 'בטל פנימי',
    markedInternal: 'סומן כהעברה פנימית — לא נספר כהוצאה',
    unmarkedInternal: 'הסימון "פנימי" בוטל',
    changeCategory: 'שנה קטגוריה',
    applyToMerchant: (merchant: string) => `החל על כל העסקאות של ‹${merchant}›`,
    categoryUpdated: (label: string) => `הקטגוריה עודכנה ל'${label}'`,
    categoryUpdatedN: (label: string, n: number) => `הקטגוריה עודכנה ל'${label}' (${n} עסקאות)`,
    changedBy: (name: string, date: string) => `שונה ידנית על ידי ${name}, ${date}`,
    categoryAria: (label: string) => `קטגוריה: ${label} — לחץ לעריכה`,
    internalChip: 'פנימי',
    cardChargeChip: 'חיוב כרטיס',
    returnChip: 'הו"ק שחזרה',
    incomeChip: 'הכנסה',
    returnNotice:
      'הוראת קבע שחזרה — הזיכוי אינו נספר כהכנסה; העמלה שנגבתה מופיעה כשורה נפרדת תחת "אחר · עמלות".',
    internalNoticeCard: 'זהו החיוב החודשי של הכרטיס — הרכישות עצמן כבר נספרות בעסקאות הכרטיס (ללא ספירה כפולה).',
    internalNoticeTransfer: 'העברה בין שני חשבונות של משק הבית — לא נספרת כהוצאה.',
    txOne: 'עסקה',
    txMany: 'עסקאות',
  },
  cats: {
    listTitle: 'קטגוריות',
    stackedQ: 'כמה הוצאנו בכל חודש, ומה הרכב ההוצאה?',
    otherMerged: 'אחר (מאוחד)',
    thisMonth: 'החודש',
    avg3: 'ממוצע 3 חודשים',
    diff: 'הפרש',
    wholeRange: (amount: string) => `כל הטווח: ${amount}`,
    manualBaseline: 'בסיס ידני',
    partialBaseline: (n: number) => `בסיס חלקי (${n})`,
    noBaselineChip: 'אין בסיס',
    noBaselineYet: 'אין בסיס עדיין — נצבר אחרי חודש מלא של נתונים',
    trendQ: (label: string) => `האם "${label}" עולה או שזה חד-פעמי?`,
    baselineLabel: (amount: string) => `בסיס ${amount}`,
    baselineThen: (amount: string) => `בסיס אז ${amount}`,
    threshold: (pct: number) => `סף חריגה: ${pct}% מעל הבסיס`,
    thresholdManual: (amount: string) => `בסיס ידני ${amount}`,
    thresholdPct: 'סף (%)',
    manualBaselineField: 'בסיס ידני (₪)',
    manualBaselinePlaceholder: 'ריק = אוטומטי',
    resetDefault: 'חזור לברירת המחדל',
    thresholdSaved: (label: string, pct: number) => `הסף לקטגוריה '${label}' עודכן ל-${pct}%`,
    thresholdSavedBase: (amount: string) => ` · בסיס ידני ${amount}`,
    thresholdReset: (label: string) => `הקטגוריה '${label}' חזרה לברירת המחדל (20%)`,
    subcats: (month: string) => `תת-קטגוריות · ${month}`,
    noSub: 'ללא תת-קטגוריה',
    topMerchants: 'בתי עסק מובילים',
    allInCategory: 'כל העסקאות בקטגוריה',
    emptyPeriod: 'אין הוצאות בתקופה',
    returnsTitle: 'החזרות ועמלות',
    returnsSub: '— הוראות קבע ושיקים שחזרו (HOK_RETURN / CHQ_RETURN)',
    returnsAll: 'כל ההחזרות',
    returns6m: 'החזרות ב-6 חודשים',
    returnsNone: 'אין החזרות',
    returnsOne: 'הוראת קבע אחת',
    returnsMany: (n: number) => `${n} הוראות קבע`,
    feesPaid: 'עמלות ששולמו',
    feesUnder: 'נספרות תחת "אחר · עמלות"',
    returnsQ: 'האם ההחזרות חוזרות על עצמן?',
    returnsPeriod: (range: string) => `${range} · עמלות ב-₪`,
    tblMonth: 'חודש',
    tblSpend: 'הוצאה',
    tblBaseline: 'בסיס',
    tblReturns: 'החזרות',
    tblFees: 'עמלות',
    tblTotal: 'סה"כ',
  },
  cards: {
    coverageQ: 'האם יש כיסוי?',
    coverageSub: (from: string, to: string) => `30 הימים הקרובים · ${from}–${to}`,
    cannotCompute: 'לא ניתן לחשב כיסוי — חבר מחדש',
    cannotComputeBody: 'החיבורים פגי תוקף. פתח את אפליקציית Financy וחבר מחדש.',
    noCards: 'אין כרטיסי אשראי מחוברים',
    missingHint: (amount: string) => `חסר ${amount} — העבר מהפיקדון (הנחיה בלבד)`,
    cycle: (from: string, to: string) => `מחזור ${from}–${to}`,
    willCharge: (d: string) => `יחויב ב-${d}`,
    dueDayFinancy: '(מ-Financy, cardDueDate)',
    dueDayDerived: '(נגזר מחיובי העו"ש — אין cardDueDate)',
    dueDayManual: '(הוגדר ידנית)',
    dueDayUnverified: 'יום חיוב לא אומת',
    dueDayLine: (due: number, cutoff: number, source: string) =>
      `חיוב ב-${due} לחודש ${source} · סגירת מחזור ב-${cutoff}`,
    expiredNotice: (at: string) => `פג תוקף · נכון ל-${at} — הנתונים האחרונים שנמשכו. חבר מחדש ב-Financy לעדכון.`,
    expectedEnd: (amount: string) => `צפי סוף מחזור ${amount}`,
    expectedParts: (variable: string, installments: string) => `(צפי משתנה ${variable} + תשלומים ${installments})`,
    utilization: 'ניצול מסגרת',
    utilizationOf: (used: string, limit: string, pct: number) => `${used} מתוך ${limit} · ${pct}%`,
    utilizationAria: (pct: number) => `ניצול מסגרת ${pct}%`,
    utilizationLeft: (amount: string) => `נותרו ${amount}`,
    utilizationHigh: 'מסגרת גבוהה',
    utilizationNear: 'קרוב למסגרת',
    installmentsSummary: (plans: number, monthly: string, last: string) =>
      `${plans} עסקאות בתשלומים · ${monthly} לחודש · האחרון יחויב ב-${last}`,
    installmentsUnavailable: 'תשלומים: לא זמין מ-Max',
    installmentPosted: (n: number, total: number, date: string) => `${n}/${total} נרשם ${date}`,
    installmentNext: (date: string, due: string) => `הבא ${date} (יחויב ${due})`,
    lastCharge: (date: string, amount: string) => `חיוב אחרון: ${date} · ${amount}`,
    bookedInBank: 'נרשם בבנק',
    chargedFromUnassigned: (mask: string) => `יורד מהעו"ש ${mask} (לא משויך למערכת זו)`,
    recentTitle: (card: string) => `חיובים אחרונים · ${card}`,
    allOfCard: (card: string) => `כל העסקאות של ${card}`,
    noCharges: 'אין חיובים',
    closedTitle: 'כרטיסים שנסגרו',
    closedBlocked: (n: number) => `${n} חסומים`,
    closedDeleted: (n: number) => `${n} מחוקים`,
    closedNote:
      'כל הכרטיסים מגיעים מ-Financy עם creditStatus (פעיל / חסום / מחוק). כרטיסים חסומים ומחוקים אינם משתתפים בכיסוי, בניצול המסגרת ובלוח התשלומים; ההיסטוריה שלהם נשמרת בעסקאות.',
    history: 'היסטוריה',
    scheduleQ: 'כמה כבר התחייבנו לחודשים הבאים, ומה הצפי?',
    schedulePeriod: (from: string, to: string, total: string, known: string) =>
      `${from} – ${to} · כל הכרטיסים · סה"כ 6 חודשים ≈ ${total} (מזה ידועים ${known})`,
    scheduleKnown: 'תשלומים ידועים',
    scheduleForecast: 'צפי מחזור (אומדן)',
    scheduleTotal6: 'סה"כ 6 חודשים',
    issuerLoans: 'קרדיט ותשלומים אצל המנפיק',
    connectionExpiredChip: 'חיבור פג תוקף',
    expiredDataNote: (names: string, date: string) => `כולל נתוני ${names} נכון ל-${date} — חבר מחדש לעדכון`,
    limit: 'מסגרת',
  },
  nw: {
    title: 'שווי נקי',
    sub: 'נכסים − התחייבויות',
    thisMonth: (amount: string, pct: string) => `החודש: ${amount} (${pct})`,
    year: (amount: string, pct: string) => `12 חודשים: ${amount} (${pct})`,
    assets: 'נכסים',
    liabilities: 'התחייבויות',
    assetsVsLiabilities: (a: string, l: string) => `נכסים ${a} מול התחייבויות ${l}`,
    chartQ: 'האם אנחנו מתקדמים?',
    chartPeriod: (from: string, to: string) => `${from} – ${to}`,
    today: 'היום',
    tblNet: 'שווי נקי',
    estimateNote: '(אומדן לפיקדון/הלוואות)',
    historyFrom: (d: string) => `היסטוריה מ-${d}`,
    estimated: 'אומדן',
    noHistory: 'ללא היסטוריה — יתרה נוכחית',
    noChange: 'ללא שינוי',
    monthChange: (amount: string) => `שינוי חודשי: ${amount}`,
    lessDebt: 'פחות חוב',
    available: 'זמין',
    bookedBalance: (amount: string) => `יתרה רשומה ${amount}`,
    overdraft: (amount: string) => `מסגרת ${amount}`,
    interest: (pct: string) => `ריבית ${pct}%`,
    renews: (d: string) => `מתחדש ב-${d}`,
    monthlyPayment: (amount: string) => `${amount}/חודש`,
    remainingPayments: (n: number) => `${n} תשלומים נותרו`,
    manualEdit: 'הזנה ידנית · ערוך',
    securities: 'ניירות ערך',
    notConnected: 'לא מחובר',
    securitiesNote:
      'החיבור לדיסקונט אינו מחזיר תיק ני"ע (הנקודה לא זמינה). כשיהיה — הוא יופיע כאן וייכלל בשווי הנקי.',
    closedLoans: 'הלוואות שנסגרו',
    groups: {
      checking: 'עובר ושב',
      savings: 'פיקדונות וחסכונות',
      securities: 'ניירות ערך',
      manual_asset: 'נכסים ידניים',
      bank_loan: 'הלוואות בנק',
      issuer_credit: 'קרדיט כרטיסים',
      card: 'כרטיסי אשראי',
      closed_loan: 'הלוואות שנסגרו',
      manual_liability: 'התחייבויות ידניות',
    } as Readonly<Record<string, string>>,
    cardFinal: (amount: string, due: string) => `מחזור סופי ${amount} (יחויב ${due})`,
    cardAccrued: (amount: string) => `+ נצבר ${amount}`,
    cardAccruedFrom: (d: string) => `נצבר מ-${d}`,
  },
  settings: {
    title: 'הגדרות וחיבורים',
    groups: {
      connections: 'חיבורים',
      accounts: 'חשבונות וכרטיסים',
      users: 'משתמשים',
      alerts: 'התראות',
      appearance: 'מראה',
      privacy: 'פרטיות וייצוא',
    } as Readonly<Record<string, string>>,
    groupsLabel: 'קבוצות הגדרות',
    connSummary: (n: number, plan: string, used: string) =>
      `${n} חיבורים · עדכון אוטומטי: פעם ביום בבוקר (${plan}) · קרדיטים החודש: ${used} בשימוש`,
    connSummaryNoCredits: (n: number) => `${n} חיבורים · עדכון אוטומטי: פעם ביום בבוקר · קרדיטים החודש: לא זמין`,
    includes: 'כולל',
    lastFetched: 'עדכון אחרון',
    consentUntil: 'תוקף ההסכמה עד',
    consentExpired: 'תוקף ההסכמה פג',
    todaySuffix: '(היום)',
    reconnect: 'פתח את Financy לחיבור מחדש',
    reconnectNote: "אין אפשרות לחבר בנק מתוך המערכת. אחרי החיבור מחדש ב-Financy, לחץ 'רענן'.",
    addConnection: 'רוצה להוסיף חיבור? הוסף אותו באפליקציית Financy — הוא יופיע כאן אחרי הרענון הבא.',
    refreshAll: 'רענן את כל החיבורים',
    assignTitle: 'שיוך חשבונות',
    assignBody:
      'שתי המערכות — "הבית שלנו" ו-LabTop Finance — מושכות מאותו חשבון Financy ומסננות לפי מספר חשבון/כרטיס. סמנו אילו חשבונות שייכים למערכת הזו. חיובי כרטיס שיורדים מהחשבון העסקי יוצגו בעסקי כ"חיוב כרטיס" בלי פירוט הרכישות (הפירוט כאן), אלא אם הכרטיס משויך לעסקי.',
    assignSwitch: 'שייך למערכת זו',
    assignAria: (name: string) => `שייך למערכת זו: ${name}`,
    assignFoot:
      'ברירת המחדל לפרטי: עו"ש ***6516, הפיקדון, כל הכרטיסים וההלוואות הפרטיות. עו"ש ***6751 והלוואות העסק שייכים ל-LabTop Finance. השינוי משפיע על המסך הראשי, על העסקאות ועל השווי הנקי.',
    assigned: (name: string) => `שויך למערכת זו: ${name}`,
    unassigned: (name: string) => `הוסר מהמערכת: ${name}`,
    cyclesTitle: 'מחזורי כרטיסים',
    cyclesBody:
      'יום החיוב של Cal מגיע מ-Financy (cardDueDate); לכרטיסים ללא cardDueDate היום נגזר מחיובי הכרטיס בעו"ש. יום סגירת המחזור אינו מגיע מ-Financy ונשמר כאן.',
    cycleSubline: (source: string) =>
      `יום סגירת מחזור · יום חיוב (שישי/שבת נדחים ליום העסקים הבא) · מקור יום החיוב: ${source}`,
    cutoff: 'סגירה',
    due: 'חיוב',
    cutoffSaved: (v: number) => `יום סגירת המחזור עודכן ל-${v}`,
    dueSaved: (v: number) => `יום החיוב עודכן ל-${v}`,
    notesTitle: 'פרטים שאינם מגיעים מ-Financy',
    notesBody:
      'מועד חידוש הפיקדון, התשלום החודשי ומספר התשלומים הנותרים בהלוואה נשמרים כאן ידנית ומוצגים במסך השווי הנקי.',
    maturityDate: 'מועד חידוש',
    monthlyPayment: 'תשלום חודשי (₪)',
    remainingPayments: 'תשלומים נותרים',
    noteSaved: 'נשמר (הזנה ידנית) — מוצג בשווי הנקי',
    invalidValue: 'ערך לא תקין',
    usersName: 'שם',
    usersEmail: 'אימייל',
    usersRole: 'תפקיד',
    usersLastLogin: 'כניסה אחרונה',
    changePassword: 'שנה סיסמה',
    pwTitle: 'שינוי סיסמה',
    pwCurrent: 'סיסמה נוכחית',
    pwNew: 'סיסמה חדשה',
    pwRepeat: 'אימות סיסמה חדשה',
    pwHint: 'לפחות 10 תווים. אחרי השינוי צריך להתחבר מחדש בכל המכשירים האחרים.',
    pwRequired: 'נא למלא את כל השדות',
    pwShort: 'הסיסמה חייבת להיות באורך 10 תווים לפחות',
    pwMismatch: 'הסיסמאות אינן זהות',
    pwSame: 'הסיסמה החדשה זהה לנוכחית',
    pwSaving: 'שומר…',
    pwSave: 'שמור סיסמה',
    pwDone: 'הסיסמה שונתה',
    remove: 'הסר',
    removeTitle: 'הסרת משתמש',
    removeBody: (n: string) => `להסיר את ${n} מהמערכת? הגישה תבוטל מיד.`,
    removed: (n: string) => `${n} הוסר/ה מהמערכת`,
    addUserNote: 'הוספת משתמש נוסף — בבנייה.',
    ruleDue: 'חיוב אשראי מתקרב',
    ruleDueSub: 'ימים לפני החיוב',
    ruleDefault: (v: number) => `ברירת מחדל: ${v}`,
    days: 'ימים',
    ruleThr: 'חריגה מהבסיס',
    ruleThrSub: 'סף ברירת מחדל באחוזים מעל ממוצע 3 חודשים',
    ruleLarge: 'הוצאה חריגה בודדת',
    ruleLargeSub: 'עסקה בודדת מעל סכום',
    ruleReturns: 'הוראת קבע / שיק שחזרו',
    ruleReturnsSub: 'התראה על כל החזרה (HOK_RETURN, CHQ_RETURN) עם העמלה שנגבתה',
    ruleStale: 'עדכניות נתונים',
    ruleStaleSub: 'התראה כשהנתונים לא עודכנו מעל',
    hours: 'שעות',
    ruleConn: 'חיבור פג תוקף',
    ruleConnSub: 'תמיד פעיל — לא ניתן לכיבוי',
    rulesSaved: 'כללי ההתראות נשמרו',
    theme: 'ערכת נושא',
    themeSub: 'בהיר / כהה / אוטומטי (לפי המכשיר)',
    themeLight: 'בהיר',
    themeDark: 'כהה',
    themeAuto: 'אוטומטי',
    textSize: 'גודל טקסט',
    textNormal: 'רגיל',
    textLarge: 'גדול',
    exactNumbers: 'הצג מספרים מדויקים (אגורות) בכרטיסים',
    on: 'פעיל',
    off: 'כבוי',
    exportTitle: 'ייצוא עסקאות (CSV)',
    exportSub: 'לתקופה',
    exportPeriodAria: 'תקופה לייצוא',
    exportPrevMonth: 'החודש הקודם',
    export: 'ייצא',
    exportStarted: 'הקובץ יורד…',
    privacyNote: 'הנתונים נשמרים בשרת שלך בלבד. Financy מחזיקה את ההסכמה הבנקאית.',
    ownerOnly: 'רק לבעלים',
    saved: 'ההגדרות נשמרו',
  },
  alerts: {
    title: 'מרכז התראות',
    filters: {
      all: 'הכול',
      unread: 'לא נקראו',
      cards: 'חיובים',
      over: 'חריגות',
      returns: 'החזרות',
      conn: 'חיבורים',
    } as Readonly<Record<string, string>>,
    filterLabel: 'סינון התראות',
    readAll: 'סמן הכול כנקרא',
    readAllDone: 'כל ההתראות סומנו כנקראו',
    empty: 'אין התראות',
    emptyBody: 'כשמשהו ידרוש תשומת לב — הוא יופיע כאן.',
    groups: { today: 'היום', week: 'השבוע', before: 'קודם' },
    unreadPrefix: 'לא נקרא: ',
    read: 'נקרא',
    markRead: 'סמן כנקרא',
    delete: 'מחק',
    deleted: 'ההתראה נמחקה',
    unreadCount: (n: number) => `${n} התראות שלא נקראו`,
    noneNew: 'אין התראות חדשות',
    allAlerts: 'לכל ההתראות',
  },
  login: {
    title: 'כניסה',
    name: 'הבית שלנו',
    tagline: 'הכסף של המשפחה, במקום אחד ובשקט',
    email: 'אימייל',
    password: 'סיסמה',
    emailRequired: 'נא להזין אימייל',
    passwordRequired: 'נא להזין סיסמה',
    remember: 'זכור אותי במכשיר הזה',
    submit: 'כניסה',
    forgot: 'שכחת סיסמה?',
    forgotToast: 'איפוס סיסמה יישלח למייל — בבנייה',
    passkey: 'כניסה ב-Passkey תתווסף בהמשך',
    showPassword: 'הצג סיסמה',
    hidePassword: 'הסתר סיסמה',
    foot: 'פרטי לגמרי · הנתונים נשארים אצלנו',
    welcome: (name: string) => `ברוך הבא, ${name}`,
    welcomeF: (name: string) => `ברוכה הבאה, ${name}`,
    invalid: 'האימייל או הסיסמה שגויים. נסה שוב.',
    rateLimited: 'יותר מדי ניסיונות. נסה שוב בעוד דקה.',
    demoHint: 'מצב הדגמה · כל אימייל וסיסמה נכנסים',
  },
  errors: {
    UNAUTHENTICATED: 'ההתחברות פגה. התחבר שוב.',
    INVALID_CREDENTIALS: 'האימייל או הסיסמה שגויים. נסה שוב.',
    FORBIDDEN: 'אין לך הרשאה לפעולה הזו.',
    CSRF: 'הבקשה נדחתה. רענן את הדף ונסה שוב.',
    NOT_FOUND: 'לא נמצא.',
    METHOD_NOT_ALLOWED: 'פעולה לא נתמכת.',
    VALIDATION: 'אחד השדות אינו תקין.',
    INVALID_PERIOD: 'התקופה שנבחרה אינה תקינה.',
    PERIOD_TOO_LONG: 'טווח מותאם מוגבל ל-12 חודשים.',
    INVALID_CURSOR: 'הרשימה השתנתה. רענן את המסך.',
    WEAK_PASSWORD: 'הסיסמה חייבת להיות באורך 10 תווים לפחות.',
    EMAIL_TAKEN: 'האימייל כבר קיים במערכת.',
    USER_LIMIT: 'המערכת הפרטית מוגבלת לשני משתמשים.',
    LAST_OWNER: 'לא ניתן להסיר את הבעלים היחיד.',
    CONFLICT: 'הפעולה מתנגשת עם המצב הנוכחי.',
    REFRESH_COOLDOWN: 'הנתונים נמשכו לאחרונה. נסה שוב מאוחר יותר.',
    SYNC_IN_PROGRESS: 'רענון כבר רץ.',
    NO_ACTIVE_CONNECTIONS: 'אין חיבורים פעילים לרענון.',
    PAYLOAD_TOO_LARGE: 'הבקשה גדולה מדי.',
    RATE_LIMITED: 'יותר מדי בקשות. נסה שוב בעוד דקה.',
    FINANCY_UNAVAILABLE: 'Financy לא הגיב. נסה שוב בעוד דקה.',
    INTERNAL: 'שגיאת שרת. נסה שוב.',
    NETWORK: 'אין חיבור לשרת. בדוק את הרשת ונסה שוב.',
  } as Readonly<Record<string, string>>,
} as const;

/** "עסקה אחת" / "3 עסקאות" — one/many with {n}. */
export function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many.replace('{n}', String(n));
}

/** "היום" · "מחר" · "בעוד יומיים" · "בעוד N ימים" (spec §4.1 ד). */
export function daysWord(n: number): string {
  if (n <= 0) return 'היום';
  if (n === 1) return 'מחר';
  if (n === 2) return 'בעוד יומיים';
  return `בעוד ${n} ימים`;
}
